import { forwardRef, type HTMLAttributes } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "@phosphor-icons/react";
import { cn } from "./cn";

/* ── Root & Trigger ── */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/* ── Portal + Overlay + Content ── */
export const DialogContent = forwardRef<
  HTMLDivElement,
  DialogPrimitive.DialogContentProps & {
    hideClose?: boolean;
    /** Under 640px the dialog fills the screen as a sheet, with a 44px Close. */
    sheetOnPhone?: boolean;
  }
>(({ className, children, hideClose, sheetOnPhone, ...props }, ref) => {
  // Dialogs that lay themselves out edge to edge (p-0) keep their own
  // header and footer; the rest get a pinned header and footer.
  const padded = !/(^|\s)p-0(\s|$)/.test(className ?? "");
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          "fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        )}
      />
      <DialogPrimitive.Content
        ref={ref}
        data-padded={padded ? "" : undefined}
        className={cn(
          "group/dialog fixed left-1/2 top-1/2 z-[70] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2",
          "rounded-xl border bg-background p-6 shadow-xl",
          // Never taller than the screen (Windows laptops at 125-150% scaling
          // are short): the body scrolls instead. vh first, dvh where known.
          "max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] overflow-y-auto",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
          "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          "duration-200",
          sheetOnPhone &&
            "max-sm:left-0 max-sm:top-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-screen max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 max-sm:pt-[calc(1.5rem+env(safe-area-inset-top))] max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=closed]:slide-out-to-top-0 max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:slide-in-from-top-0",
          className
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close
            data-dialog-close
            // Above the pinned header, which otherwise paints over the top
            // half of the icon and leaves what looks like a chevron.
            className={cn(
              "absolute right-4 top-4 z-20 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              sheetOnPhone &&
                "max-sm:right-1 max-sm:top-[calc(0.5rem+env(safe-area-inset-top))] max-sm:flex max-sm:h-[44px] max-sm:w-[44px] max-sm:items-center max-sm:justify-center"
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
DialogContent.displayName = "DialogContent";

/* ── Header ── */
export function DialogHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        // Stays at the top while a tall body scrolls under it.
        "group-data-[padded]/dialog:sticky group-data-[padded]/dialog:top-[-1.5rem] group-data-[padded]/dialog:z-10 group-data-[padded]/dialog:-mx-6 group-data-[padded]/dialog:-mt-6 group-data-[padded]/dialog:mb-0 group-data-[padded]/dialog:bg-background group-data-[padded]/dialog:px-6 group-data-[padded]/dialog:pb-3 group-data-[padded]/dialog:pt-6",
        className
      )}
      {...props}
    />
  );
}

/* ── Title ── */
export const DialogTitle = forwardRef<
  HTMLHeadingElement,
  DialogPrimitive.DialogTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

/* ── Description ── */
export const DialogDescription = forwardRef<
  HTMLParagraphElement,
  DialogPrimitive.DialogDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

/* ── Footer ── */
export function DialogFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 pt-4",
        // Stays at the bottom, so the buttons are always in reach.
        "group-data-[padded]/dialog:sticky group-data-[padded]/dialog:bottom-[-1.5rem] group-data-[padded]/dialog:z-10 group-data-[padded]/dialog:-mx-6 group-data-[padded]/dialog:-mb-6 group-data-[padded]/dialog:bg-background group-data-[padded]/dialog:px-6 group-data-[padded]/dialog:pb-6",
        className
      )}
      {...props}
    />
  );
}
